import React, { useState, useRef } from 'react';
import PropTypes, { any } from 'prop-types';

import { getSetOptions } from './consts';
import SwapIcon from './SwapIcon';
import { getTieDisplay } from '../../../utils/postScore';
import { sportTypeIds } from '../../../utils/eventConstants';

import styles from './eventDeskPostScore.scss';
import { isTennisSportTypeEvent } from '../../../utils/events';

const EditMatchScoreGrid = (props) => {
    const {
        onSetMatchSets,
        sets,
        handleSwapPositions,
        teams,
        sportTypeId,
        attachRef,
        onKeyDown,
        setCurrentSet,
        'data-testid': dataTestId,
    } = props;

    const [focusedSetId, setFocusedSetId] = useState(null);
    const setWrapperRefs = useRef({});

    const onChangeMatchSet = (set, position) => (e) => {
        onSetMatchSets(e, set, position);
    };

    const handleSetFocus = (setId) => {
        setFocusedSetId(setId);
        setCurrentSet(setId);
    };

    const handleSetBlur = (setId, e) => {
        const wrapper = setWrapperRefs.current[setId];
        if (wrapper?.contains(e.relatedTarget)) {
            return;
        }
        setFocusedSetId(null);
    };

    const onClickTieValue = (game) => () => {
        const input = document.getElementById(
            `matchScore-${game?.label}-${game?.position}`,
        );
        if (input) {
            input.focus();
        }
    };

    const setOptions = getSetOptions(sportTypeId);
    const isTennisEvent = isTennisSportTypeEvent(sportTypeId);

    return (
        <div className={styles.editMatchScoreGridWrapper} data-testid={dataTestId}>
            <p className={styles.editMatchScoreGridPlayerText} data-testid={dataTestId && `${dataTestId}.player1-name`}>{teams[0]?.displayName}</p>
            <div className={styles.editMatchScoreGridContainer}>
                <div className={styles.editMatchScoreGridContainer}>
                    {setOptions.map(setOption => {
                        const setId = setOption?.id;
                        const setData = sets?.find(set => set?.id === setId) || {};
                        const { isTie, showTie } = getTieDisplay(setData, focusedSetId, sportTypeId);
                        const showTieInputs = isTennisEvent && showTie;

                        return (
                            <div
                                key={setId}
                                className={styles.editMatchScoreGridContainer}
                                ref={(el) => { setWrapperRefs.current[setId] = el; }}
                                onFocus={() => handleSetFocus(setId)}
                                onBlur={(e) => handleSetBlur(setId, e)}
                            >
                                <div className={styles.editMatchScoreGridItem}>
                                    {setOption?.games?.map(game => {
                                        const { label, position, tieBreakPosition } = game || {};

                                        return (
                                            <div
                                                className={styles.editMatchScoreGridCell}
                                                key={`${game.id}`}
                                            >
                                                <label
                                                    className={styles.editMatchScoreGridLabel}
                                                    htmlFor={`matchScore-${label}-${position}`}
                                                >
                                                    {label}
                                                </label>
                                                <input
                                                    id={`matchScore-${label}-${position}`}
                                                    className={styles.editMatchScoreGridInput}
                                                    onChange={onChangeMatchSet(setData, position)}
                                                    onKeyDown={event => onKeyDown(setData, position, event)}
                                                    value={setData[position]}
                                                    ref={node => attachRef(setId, position, node)}
                                                    data-testid={dataTestId && `${dataTestId}.set-${setId}.score-${label}-${position}`}
                                                />
                                                {isTie && !showTieInputs && setData[tieBreakPosition] && (
                                                    <div
                                                        className={styles.editMatchScoreGridTieValue}
                                                        onClick={onClickTieValue(game)}
                                                        data-testid={dataTestId && `${dataTestId}.set-${setId}.tiebreak-${label}`}
                                                    >
                                                        {setData[tieBreakPosition]}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {showTieInputs && (
                                    <div className={styles.editMatchScoreGridItem}>
                                        {setOption?.games?.map(game => {
                                            const { label, tieBreakPosition } = game || {};
                                            return (
                                                <div
                                                    className={styles.editMatchScoreGridCell}
                                                    key={`${game?.label}-${tieBreakPosition}`}
                                                >
                                                    <label
                                                        className={styles.editMatchScoreGridLabel}
                                                        htmlFor={`matchScore-${label}-${tieBreakPosition}`}
                                                    >
                                                        Tiebreak
                                                    </label>
                                                    <input
                                                        id={`matchScore-${label}-${tieBreakPosition}`}
                                                        className={styles.editMatchScoreGridInput}
                                                        onChange={onChangeMatchSet(setData, tieBreakPosition)}
                                                        onKeyDown={event => onKeyDown(setData, tieBreakPosition, event)}
                                                        value={setData[tieBreakPosition]}
                                                        ref={node => attachRef(setId, tieBreakPosition, node)}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <button
                    onClick={handleSwapPositions}
                    className={styles.editMatchScoreSwapButton}
                    type="button"
                    data-testid={dataTestId && `${dataTestId}.swap-btn`}
                >
                    <SwapIcon />
                </button>
            </div>
            <p className={styles.editMatchScoreGridPlayerText} data-testid={dataTestId && `${dataTestId}.player2-name`}>{teams[1]?.displayName}</p>
        </div>
    );
};

EditMatchScoreGrid.propTypes = {
    onSetMatchSets: PropTypes.func.isRequired,
    handleSwapPositions: PropTypes.func.isRequired,
    sets: PropTypes.arrayOf(any),
    teams: PropTypes.arrayOf(any),
    sportTypeId: PropTypes.number,
    attachRef: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    setCurrentSet: PropTypes.func.isRequired,
    'data-testid': PropTypes.string,
};

EditMatchScoreGrid.defaultProps = {
    sets: [],
    teams: [],
    sportTypeId: sportTypeIds.TENNIS,
    'data-testid': undefined,
};

export default EditMatchScoreGrid;
